import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { MeasurementDatesService } from 'api';
import {
  MERGED_MEASUREMENT_DATES_BY_FIRM_DEFAULT_ERROR_MESSAGE,
  MERGED_MEASUREMENT_DATES_BY_FIRM_QUERY_KEY,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import {
  GetMergedMeasurementDatesByFirmParams,
  GetMergedMeasurementDatesByFirmResponse,
  GetMergedMeasurementDatesByFirmReturn,
  UseGetMergedMeasurementDatesByFirmParams,
} from './useGetMergedMeasurementDatesByFirm.types';

export const getMergedMeasurementDatesByFirm = async (
  params: GetMergedMeasurementDatesByFirmParams
): Promise<GetMergedMeasurementDatesByFirmResponse | null> => {
  const { firmId } = params;

  if (isNil(firmId)) {
    return throwError(MERGED_MEASUREMENT_DATES_BY_FIRM_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const measurementDates = await MeasurementDatesService.apiMeasurementDatesByFirmRead(toString(firmId));

    if (!measurementDates) {
      return throwError(MERGED_MEASUREMENT_DATES_BY_FIRM_DEFAULT_ERROR_MESSAGE);
    }

    return measurementDates;
  } catch (error) {
    return throwError(MERGED_MEASUREMENT_DATES_BY_FIRM_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetMergedMeasurementDatesByFirm = (
  params: UseGetMergedMeasurementDatesByFirmParams
): GetMergedMeasurementDatesByFirmReturn => {
  const { firmId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch, fetchStatus } = useQuery<
    GetMergedMeasurementDatesByFirmReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && shouldQueryAutomatically,
    queryFn: () => getMergedMeasurementDatesByFirm({ firmId }),
    queryKey: [MERGED_MEASUREMENT_DATES_BY_FIRM_QUERY_KEY, firmId],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
  });

  const successData = useMemo(() => {
    if (fetchStatus === 'idle') {
      return data;
    }

    return null;
  }, [data, fetchStatus]);

  return {
    data: successData,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetMergedMeasurementDatesByFirm;
